import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { useCallback } from "react";
import { 
    FindAllMerchantsOnboardingAdminQueryQuery, 
    QueryFindAllMerchantsOnboardingAdminArgs
} from "../../../../../../schemaTypes";

export const findAllMerchantsOnboardingAdminQuery = gql`
  query FindAllMerchantsOnboardingAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllMerchantsOnboardingAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        adminId
        merchantId
        firstname
        lastname
        companyName
        currentStep
        status
        isVerified
        websiteOrSocials
        email
        phone
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllMerchantsOnboardingAdmin {
  data: FindAllMerchantsOnboardingAdminQueryQuery["findAllMerchantsOnboardingAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreMerchants: boolean;
}

interface Props {
  variables: QueryFindAllMerchantsOnboardingAdminArgs;
  children: (data: WithFindAllMerchantsOnboardingAdmin) => React.ReactElement | null;
}

export const FindAllMerchantsOnboardingAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllMerchantsOnboardingAdminQueryQuery,
    QueryFindAllMerchantsOnboardingAdminArgs
  >(findAllMerchantsOnboardingAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllMerchantsOnboardingAdminData = data?.findAllMerchantsOnboardingAdmin?.data || [];
  const page = data?.findAllMerchantsOnboardingAdmin?.page || variables.page;
  const pageSize = data?.findAllMerchantsOnboardingAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllMerchantsOnboardingAdmin?.total || 0;
  const totalPages = data?.findAllMerchantsOnboardingAdmin?.totalPages || 0;
  const hasMoreMerchants = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreMerchants) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllMerchantsOnboardingAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllMerchantsOnboardingAdmin: {
            ...fetchMoreResult?.findAllMerchantsOnboardingAdmin,
            data: [
              ...(prev?.findAllMerchantsOnboardingAdmin?.data || []),
              ...(fetchMoreResult?.findAllMerchantsOnboardingAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreMerchants]);

  return children({
    data: findAllMerchantsOnboardingAdminData as any,
    loading,
    loadMore,
    hasMoreMerchants,
    page,
    pageSize,
    total,
    totalPages,
  });
};